import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

/**
 * PopupAlert - Reusable centered modal popup replacing browser alert() / confirm()
 *
 * Props:
 *  popup        : { message: string, type: 'success' | 'error' | 'confirm' | 'info' } | null
 *  onClose      : () => void
 *  onConfirm    : () => void  (only used when type === 'confirm')
 *  confirmLabel : string  (optional, default "Delete")
 */
const ICONS = {
  success: "✨",
  error: "❌",
  confirm: "⚠️",
  info: "ℹ️",
};

const COLORS = {
  success: "#10b981",
  error: "#ef4444",
  confirm: "#f59e0b",
  info: "#3b82f6",
};

const PopupAlert = ({ popup, onClose, onConfirm, confirmLabel = "Delete" }) => {
  if (!popup) return null;

  const color = COLORS[popup.type] || COLORS.info;
  const icon = ICONS[popup.type] || ICONS.info;
  const isConfirm = popup.type === "confirm";

  return (
    <AnimatePresence>
      <motion.div
        key="popup-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={!isConfirm ? onClose : undefined}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.6)",
          zIndex: 99999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backdropFilter: "blur(5px)",
          WebkitBackdropFilter: "blur(5px)",
        }}
      >
        <motion.div
          key="popup-box"
          initial={{ scale: 0.7, opacity: 0, y: 50 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.7, opacity: 0, y: 50 }}
          transition={{ type: "spring", stiffness: 340, damping: 26 }}
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "linear-gradient(145deg, #1e293b 0%, #0f172a 100%)",
            border: `1.5px solid ${color}44`,
            borderRadius: "22px",
            padding: "2.6rem 3rem",
            maxWidth: "440px",
            width: "90%",
            boxShadow: `0 30px 70px rgba(0,0,0,0.55), 0 0 0 1px ${color}1a`,
            textAlign: "center",
            position: "relative",
          }}
        >
          {/* Close button (non-confirm only) */}
          {!isConfirm && (
            <button
              onClick={onClose}
              style={{
                position: "absolute",
                top: "1rem",
                right: "1rem",
                background: "rgba(255,255,255,0.07)",
                border: "none",
                borderRadius: "50%",
                width: "32px",
                height: "32px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#94a3b8",
              }}
            >
              <X size={16} />
            </button>
          )}

          {/* Emoji Icon */}
          <motion.div
            initial={{ scale: 0, rotate: -10 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.08, type: "spring", stiffness: 380 }}
            style={{ fontSize: "3.2rem", marginBottom: "1rem", lineHeight: 1 }}
          >
            {icon}
          </motion.div>

          {/* Message */}
          <p
            style={{
              color: "#e2e8f0",
              fontSize: "1.05rem",
              fontWeight: 500,
              lineHeight: 1.65,
              margin: "0 0 1.8rem 0",
            }}
          >
            {popup.message}
          </p>

          {/* Action Buttons */}
          {isConfirm ? (
            <div style={{ display: "flex", gap: "1rem", justifyContent: "center" }}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.96 }}
                onClick={onClose}
                style={{
                  padding: "0.65rem 1.8rem",
                  borderRadius: "11px",
                  border: "1px solid #334155",
                  background: "transparent",
                  color: "#94a3b8",
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: "0.95rem",
                }}
              >
                Cancel
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.96 }}
                onClick={onConfirm}
                style={{
                  padding: "0.65rem 1.8rem",
                  borderRadius: "11px",
                  border: "none",
                  background: "linear-gradient(135deg, #ef4444, #dc2626)",
                  color: "#fff",
                  cursor: "pointer",
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  boxShadow: "0 4px 18px rgba(239,68,68,0.38)",
                }}
              >
                {confirmLabel}
              </motion.button>
            </div>
          ) : (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.96 }}
              onClick={onClose}
              style={{
                padding: "0.7rem 2.8rem",
                borderRadius: "11px",
                border: "none",
                background: `linear-gradient(135deg, ${color}, ${color}cc)`,
                color: "#fff",
                cursor: "pointer",
                fontWeight: 700,
                fontSize: "1rem",
                boxShadow: `0 4px 22px ${color}44`,
              }}
            >
              OK
            </motion.button>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * usePopup — Custom hook to manage popup state
 * Returns: { popup, showPopup, showConfirm, closePopup, handleConfirm, confirmCallback }
 */
export const usePopup = () => {
  const [popup, setPopup] = React.useState(null);
  const [confirmCallback, setConfirmCallback] = React.useState(null);

  const showPopup = (message, type = "info") => setPopup({ message, type });

  const showConfirm = (message, onConfirm) => {
    setConfirmCallback(() => onConfirm);
    setPopup({ message, type: "confirm" });
  };

  const closePopup = () => {
    setPopup(null);
    setConfirmCallback(null);
  };

  const handleConfirm = () => {
    if (confirmCallback) confirmCallback();
    closePopup();
  };

  return { popup, showPopup, showConfirm, closePopup, handleConfirm };
};

export default PopupAlert;
